import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookmarkCheck, ChevronDown, LogIn, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

const Avatar = ({ user, size = 26 }) =>
  user.picture ? (
    <img
      src={user.picture}
      alt=""
      referrerPolicy="no-referrer"
      className="rounded-full object-cover border border-[#D2D5DA] bg-white"
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      className="rounded-full bg-[#0F1117] text-white font-mono-label text-[9px] tracking-[0.08em] inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {initials(user.name)}
    </span>
  );

export const SignInButton = ({ className = "", testId = "header-sign-in-btn", label = "Sign in" }) => {
  const { login } = useAuth();
  return (
    <button
      type="button"
      onClick={() => login()}
      data-testid={testId}
      className={`chrome-btn inline-flex items-center justify-center gap-2 font-mono-label text-[10.5px] md:text-[11px] tracking-[0.14em] px-3.5 md:px-4 py-2.5 whitespace-nowrap max-w-full ${className}`}
    >
      <LogIn size={13} aria-hidden="true" />
      {label}
    </button>
  );
};

export function UserMenu() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (loading) return <span data-testid="auth-loading" className="w-[86px] h-9" aria-hidden />;
  if (!user) return <SignInButton />;

  const firstName = (user.name || user.email).split(" ")[0];
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="header-user-menu-btn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="chrome-btn inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 font-mono-label text-[10.5px] tracking-[0.12em] whitespace-nowrap"
      >
        <Avatar user={user} />
        <span data-testid="header-user-name" className="hidden md:inline max-w-[110px] truncate">
          {firstName}
        </span>
        <ChevronDown size={12} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute right-0 top-full pt-2 w-[240px] transition-all duration-200 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
      >
        <div data-testid="header-user-menu" className="bg-white border border-[#D2D5DA] shadow-[0_24px_48px_rgba(15,17,23,0.14)]">
          <div className="flex items-center gap-3 px-4 py-3 border-b hairline">
            <Avatar user={user} size={34} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#0F1117] truncate">{user.name}</div>
              <div data-testid="header-user-email" className="text-[11px] text-[#71747B] truncate">{user.email}</div>
            </div>
          </div>
          <Link
            to="/my-jobs"
            data-testid="header-my-jobs-link"
            onClick={() => setOpen(false)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#0F1117] hover:bg-[#F4F4F6] transition-colors border-b hairline"
          >
            My Jobs
            <BookmarkCheck size={14} className="text-[#0FBF77]" />
          </Link>
          <button
            type="button"
            data-testid="header-sign-out-btn"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-[#0F1117] hover:bg-[#F4F4F6] transition-colors"
          >
            Sign out
            <LogOut size={14} className="text-[#8A8D94]" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function MobileAccountRow({ onNavigate }) {
  const { user, loading, logout } = useAuth();
  if (loading) return null;
  if (!user) {
    return (
      <SignInButton
        testId="mobile-sign-in-btn"
        label="Sign in with email or Google"
        className="mt-3 w-full py-3.5"
      />
    );
  }
  return (
    <div data-testid="mobile-account-row" className="mt-3 flex items-center gap-3 border hairline px-4 py-3 bg-white">
      <Avatar user={user} size={34} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#0F1117] truncate">{user.name}</div>
        <div className="text-[11px] text-[#71747B] truncate">{user.email}</div>
      </div>
      <Link
        to="/my-jobs"
        data-testid="mobile-my-jobs-link"
        onClick={() => onNavigate?.()}
        className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#0A8F5A] hover:text-[#0F1117] inline-flex items-center gap-1.5"
      >
        <BookmarkCheck size={13} />
        My Jobs
      </Link>
      <button
        type="button"
        data-testid="mobile-sign-out-btn"
        onClick={() => {
          onNavigate?.();
          logout();
        }}
        className="font-mono-label text-[10px] tracking-[0.14em] uppercase text-[#5F6168] hover:text-[#0F1117] inline-flex items-center gap-1.5"
      >
        <LogOut size={13} />
        Sign out
      </button>
    </div>
  );
}
